import { useState } from "react";
import { useParams } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AnswerForm } from "./AnswerForm";
import { LoadingLines } from "./Loading";
import { runDate } from "./MethodNavigation";
import { ProductionApiError, productionApi } from "./production-api";

type Closed = "answered" | "expired";
const closedText: Record<Closed, string> = { answered: "This question was already answered.", expired: "This question expired." };

/** The page of a one-time answer link. It needs no sign-in. */
export function AnswerPage() {
  const { token } = useParams({ strict: false }) as { token: string };
  const link = useQuery({ queryKey: ["answer-link", token], queryFn: () => productionApi.link(token), retry: false, refetchOnWindowFocus: false });
  const [closed, setClosed] = useState<Closed | null>(null);
  const answer = useMutation({
    mutationFn: (value: Record<string, unknown>) => productionApi.answerLink(token, value),
    onError: error => { if (error instanceof ProductionApiError && error.status === 410) setClosed(error.code === "expired" ? "expired" : "answered"); },
  });

  const status = closed ?? (link.data && link.data.status !== "waiting" ? link.data.status : null);
  let body;
  if (link.isPending) body = <LoadingLines label="Loading the question" />;
  else if (link.error) body = <p role="alert">{link.error instanceof ProductionApiError && link.error.status === 404 ? "This link is not valid." : link.error.message}</p>;
  else if (answer.isSuccess) body = <p role="status">Thank you. The run continues.</p>;
  else if (status) body = <p role="status">{closedText[status]}</p>;
  else body = <>
    {link.data.question && <p className="answer-question">{link.data.question}</p>}
    <p className="answer-note">You can answer one time.{link.data.expires_at ? ` The question expires ${runDate(link.data.expires_at)}.` : ""}</p>
    <AnswerForm form={link.data.form} pending={answer.isPending} error={answer.error?.message} onSubmit={value => answer.mutate(value)} />
  </>;

  return <main className="answer-page">
    <div className="answer-card">
      <h1>{link.data?.method_name ?? "Question"}</h1>
      {body}
    </div>
  </main>;
}
